import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, MapPin, XCircle } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const DELIVERY_MODE = { livraison: "Livraison", retrait: "Retrait au dépôt (gratuit, sur rendez-vous)" };

async function openDoc(d, view) {
  try {
    const r = await api.get(`/client/documents/${d.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (view) {
      window.open(url, "_blank", "noopener");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d.label || "document").replace(/[^\w\- ]/g, "")}.pdf`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error("Document indisponible pour le moment");
  }
}

export default function ClientReservationPage() {
  const { reservationId } = useParams();
  const [r, setR] = useState(null);
  const [pay, setPay] = useState(null);
  const [docs, setDocs] = useState([]);
  const [history, setHistory] = useState([]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/client/reservations/${reservationId}`).then((res) => setR(res.data)).catch(() => setR(false));
    api.get(`/client/reservations/${reservationId}/payments`).then((res) => setPay(res.data)).catch(() => setPay(null));
    api.get("/client/documents", { params: { reservation_id: reservationId } }).then((res) => setDocs(res.data)).catch(() => setDocs([]));
    api.get(`/client/reservations/${reservationId}/history`).then((res) => setHistory(res.data)).catch(() => setHistory([]));
  };
  useEffect(() => { load(); }, [reservationId]);

  if (r === null) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="reservation-loading">Chargement…</div>;
  if (r === false) return <EmptyState title="Réservation introuvable" />;

  const ev = r.event || {};
  const totals = r.totals || {};
  const canCancel = ["confirmee", "en_attente_finalisation"].includes(r.status);

  const submitCancellation = async () => {
    if (cancelReason.trim().length < 3) return;
    setSending(true);
    try {
      await api.post(`/client/reservations/${reservationId}/request-cancellation`, { reason: cancelReason.trim() });
      toast.success("Demande d'annulation envoyée — Art Event vous répondra après étude (CGV)");
      setCancelOpen(false);
      setCancelReason("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="client-reservation-page">
      <Link to={r.event_id ? `/compte/evenements/${r.event_id}` : "/compte"} className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="reservation-back">
        <ArrowLeft className="h-4 w-4" /> Retour à mon événement
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-2xl text-deepgreen" data-testid="reservation-ref">{r.ref}</h2>
        <StatusBadge status={r.status} />
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="reservation-dates">
        {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ""}
        {ev.venue_address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-sage" /> {ev.venue_address}</span>}
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card-ae p-6" data-testid="reservation-items">
            <h3 className="font-heading text-lg text-deepgreen">Articles réservés</h3>
            <ul className="mt-3 space-y-3">
              {(r.items || []).map((it, i) => (
                <li key={i} className="rounded-lg bg-cream px-4 py-3" data-testid={`reservation-item-${i}`}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold text-deepgreen">{it.name} × {it.qty}</span>
                    <span>{((it.unit_price ?? it.price ?? 0) * it.qty).toFixed(2)} €</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{(it.unit_price ?? it.price ?? 0).toFixed(2)} € / unité</p>
                  {(it.options || []).map((o, j) => (
                    <p key={j} className="mt-0.5 text-xs text-muted-foreground">Option : {o.name} (+{(o.price ?? 0).toFixed(2)} €)</p>
                  ))}
                  {(it.option_choices || []).map((o, j) => (
                    <p key={j} className="mt-0.5 text-xs text-muted-foreground">{o.group || o.group_name} : {o.choice || o.choice_label} (+{(o.price ?? o.price_delta ?? 0).toFixed(2)} €)</p>
                  ))}
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-1.5 border-t border-border pt-3 text-sm">
              {totals.livraison != null && <p className="flex justify-between"><span className="text-muted-foreground">Livraison</span><span>{(totals.livraison ?? 0).toFixed(2)} €</span></p>}
              <p className="flex justify-between text-base"><span className="font-bold text-deepgreen">Total TTC</span><span className="font-bold text-deepgreen" data-testid="reservation-total">{(totals.total_ttc ?? 0).toFixed(2)} €</span></p>
              <p className="flex justify-between"><span className="text-muted-foreground">Acompte 30 %</span><span>{(totals.acompte ?? 0).toFixed(2)} €</span></p>
              <p className="flex justify-between"><span className="text-muted-foreground">Solde</span><span>{(totals.solde ?? 0).toFixed(2)} €</span></p>
              {(totals.caution_separee ?? 0) > 0 && (
                <p className="flex justify-between"><span className="text-muted-foreground">Caution (empreinte séparée, non débitée)</span><span>{totals.caution_separee.toFixed(2)} €</span></p>
              )}
            </div>
          </div>

          <div className="card-ae p-6" data-testid="reservation-delivery">
            <h3 className="font-heading text-lg text-deepgreen">Livraison / retrait</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {DELIVERY_MODE[r.delivery?.mode] || (r.delivery?.mode ?? "Retrait au dépôt (gratuit, sur rendez-vous)")}
              {r.delivery?.address ? ` — ${r.delivery.address}` : ""}
            </p>
          </div>

          <div className="card-ae p-6" data-testid="reservation-documents">
            <h3 className="font-heading text-lg text-deepgreen">Documents</h3>
            {docs.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Vos documents (récapitulatif, CGV acceptées, devis) apparaîtront ici automatiquement.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid="reservation-documents-list">
                {docs.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-cream px-4 py-2.5" data-testid={`reservation-doc-${d.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-deepgreen">{d.label}</p>
                      <p className="text-[11px] text-muted-foreground">{(d.created_at || "").slice(0, 10)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => openDoc(d, true)} className="btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid={`reservation-doc-view-${d.id}`}>Voir</button>
                      <button type="button" onClick={() => openDoc(d, false)} className="btn-primary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid={`reservation-doc-download-${d.id}`}>
                        <Download className="h-3.5 w-3.5" /> Télécharger
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card-ae p-6" data-testid="reservation-history">
            <h3 className="font-heading text-lg text-deepgreen">Historique</h3>
            {history.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Aucun événement enregistré pour le moment.</p>
            ) : (
              <ul className="mt-3 space-y-2" data-testid="reservation-history-list">
                {history.map((h, i) => (
                  <li key={i} className="rounded-lg bg-cream px-4 py-2.5 text-sm" data-testid={`reservation-history-row-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-deepgreen">{h.action}</span>
                      <span className="text-xs text-muted-foreground">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{h.origin}{h.detail ? ` — ${h.detail}` : ""}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside className="space-y-6">
          {pay && (
            <div className="card-ae p-6" data-testid="reservation-payment-summary">
              <h3 className="font-heading text-lg text-deepgreen">Paiements</h3>
              <p className="mt-2 text-sm"><span className="text-muted-foreground">Reste à régler :</span> <strong>{(pay.remaining_due ?? 0).toFixed(2)} €</strong></p>
              {pay.deposit && (
                <p className="mt-2 text-sm"><span className="text-muted-foreground">Caution :</span> <strong>{(pay.deposit.amount ?? 0).toFixed(2)} €</strong> — empreinte séparée</p>
              )}
              <p className="mt-3 text-xs text-muted-foreground">Le règlement en ligne (solde, empreinte) se fait depuis l'onglet Paiements de votre événement.</p>
            </div>
          )}
          <div className="card-ae p-6" data-testid="reservation-actions">
            <h3 className="font-heading text-lg text-deepgreen">Actions</h3>
            {r.status === "annulation_demandee" && (
              <p className="mt-3 rounded-lg bg-gold-light px-4 py-2.5 text-xs font-semibold text-deepgreen" data-testid="reservation-cancellation-pending">
                Demande d'annulation en cours d'étude par Art Event.
              </p>
            )}
            {canCancel && !cancelOpen && (
              <button type="button" onClick={() => setCancelOpen(true)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-red-600 transition-colors hover:bg-red-50" data-testid="reservation-cancel-open">
                <XCircle className="h-4 w-4" /> Demander l'annulation
              </button>
            )}
            {canCancel && cancelOpen && (
              <div className="mt-3 space-y-3" data-testid="reservation-cancel-form">
                <p className="text-xs text-muted-foreground">Votre demande sera étudiée par Art Event — les suites financières suivent les CGV acceptées. Aucun remboursement automatique.</p>
                <textarea className="input-ae min-h-20" placeholder="Motif de votre demande (obligatoire)…" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={1000} data-testid="reservation-cancel-reason" />
                <div className="flex gap-2">
                  <button type="button" onClick={submitCancellation} disabled={sending || cancelReason.trim().length < 3} className="btn-primary-ae flex-1 !py-2.5 !text-xs disabled:opacity-50" data-testid="reservation-cancel-submit">
                    {sending ? "Envoi…" : "Envoyer la demande"}
                  </button>
                  <button type="button" onClick={() => setCancelOpen(false)} className="btn-secondary-ae !py-2.5 !text-xs" data-testid="reservation-cancel-abort">Annuler</button>
                </div>
              </div>
            )}
            {!canCancel && r.status !== "annulation_demandee" && (
              <p className="mt-3 text-xs text-muted-foreground">Pour toute question sur cette réservation, contactez Art Event via la messagerie de votre événement.</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
